import React, { useCallback, useState } from 'react';
import { Snackbar, Alert, AlertTitle, Button, Stack, Slide, Box, IconButton, Tooltip } from '@mui/material';
import type { SlideProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  useDueReminders,
  DUE_REMINDER_SOURCE_TEXTS,
  formatDueReminderTotals,
  type DueReminderGroup,
} from '../../contexts/DueRemindersContext';
import { buildReminderHref, reminderSearchFromItem } from '../../utils/reminderDeepLink';

/**
 * DueRemindersNotifier — vade hatırlatmalarının sağ alttaki uyarı bandı.
 *
 * Veriyi DueRemindersContext'ten alır (Header'daki bildirim zili ile AYNI kaynak).
 * Bandın kapatma damgası kaynak bazlı ve günlüktür: kredi kartı uyarısını kapatmak
 * çek/senet uyarısını gizlemez, kalem ödenmediği sürece ertesi gün yeniden gelir.
 *
 * NOT: Kapatma yalnızca bu bandı susturur; zil listesinde kalem durmaya devam eder
 * (zil canlı durumu gösterir, kapatılabilir bir bildirim kutusu değildir).
 */

const todayStamp = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const SlideLeft = (props: SlideProps) => <Slide {...props} direction="left" />;

const DueRemindersNotifier: React.FC = () => {
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const { i18n } = useTranslation();
  const { groups, enabled } = useDueReminders();

  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const companyId = userDetails?.companyId;
  // Bu oturumda elle kapatılanlar — damga yazılana kadar anında gizlemek için.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const stampKeyFor = useCallback(
    (source: string) => (companyId ? `dueReminderNotifiedAt:${companyId}:${source}` : null),
    [companyId]
  );

  const isStampedToday = useCallback(
    (source: string) => {
      const key = stampKeyFor(source);
      if (!key) return false;
      try {
        return localStorage.getItem(key) === todayStamp();
      } catch {
        return false;
      }
    },
    [stampKeyFor]
  );

  const dismiss = (source: string) => {
    const key = stampKeyFor(source);
    if (key) {
      try {
        localStorage.setItem(key, todayStamp());
      } catch { /* localStorage erişilemezse sessizce geç */ }
    }
    setDismissed((prev) => new Set(prev).add(source));
  };

  const goTo = (group: DueReminderGroup) => {
    dismiss(group.source);
    const hasOverdue = group.overdueCount > 0;
    const items = group.items || [];
    const target = hasOverdue
      ? items.find((i) => i.daysToDue < 0) ?? items[0]
      : items.find((i) => i.daysToDue >= 0) ?? items[0];

    const searchText = target
      ? reminderSearchFromItem(target)
      : reminderSearchFromItem({
          label: hasOverdue ? group.mostOverdueLabel || '' : group.nearestLabel || '',
        });

    navigate(buildReminderHref(group.route, searchText));
  };

  const visible = groups.filter((g) => !dismissed.has(g.source) && !isStampedToday(g.source));

  if (!enabled || visible.length === 0) return null;

  return (
    <Snackbar
      open
      autoHideDuration={null}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      TransitionComponent={SlideLeft}
      sx={{ maxWidth: 480 }}
    >
      <Stack spacing={1} sx={{ maxHeight: '70vh', overflowY: 'auto', width: '100%' }}>
        {visible.map((group) => {
          const texts = DUE_REMINDER_SOURCE_TEXTS[group.source];
          if (!texts) return null;

          const name = tt(texts.nameTr, texts.nameEn);
          const hasOverdue = group.overdueCount > 0;

          const title = hasOverdue
            ? tt(
                `⚠️ Vadesi GEÇMİŞ ${group.overdueCount} ${name} — toplam ${formatDueReminderTotals(group.overdueTotals, localeCode)}`,
                `⚠️ ${group.overdueCount} ${name} OVERDUE — total ${formatDueReminderTotals(group.overdueTotals, localeCode)}`
              )
            : tt(
                `⏰ Vadesi yaklaşan ${group.upcomingCount} ${name} — toplam ${formatDueReminderTotals(group.upcomingTotals, localeCode)}`,
                `⏰ ${group.upcomingCount} ${name} due soon — total ${formatDueReminderTotals(group.upcomingTotals, localeCode)}`
              );

          const detail = hasOverdue
            ? tt(
                `${group.mostOverdueLabel ?? ''} — ${group.mostOverdueDays} gün gecikti`,
                `${group.mostOverdueLabel ?? ''} — ${group.mostOverdueDays} day(s) late`
              )
            : group.nearestDueDate
              ? tt(
                  `En yakın vade: ${group.nearestLabel ?? ''}, ${new Date(group.nearestDueDate).toLocaleDateString(localeCode)}`,
                  `Nearest due: ${group.nearestLabel ?? ''}, ${new Date(group.nearestDueDate).toLocaleDateString(localeCode)}`
                )
              : '';

          // Gecikmiş kalem varken ayrıca yaklaşan da varsa tek satırda özetle
          const extra = hasOverdue && group.upcomingCount > 0
            ? tt(` · Ayrıca ${group.upcomingCount} kalemin vadesi yaklaşıyor`, ` · ${group.upcomingCount} more due soon`)
            : '';

          return (
            <Alert
              key={group.source}
              severity={hasOverdue ? 'error' : 'warning'}
              variant="filled"
              // NOT: MUI'de `action` verildiğinde varsayılan kapatma ikonu render
              // edilmez; bu yüzden "Git" ile kapatma düğmesi birlikte veriliyor.
              action={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => goTo(group)}
                    sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                  >
                    {tt(texts.goTr, texts.goEn)}
                  </Button>
                  <Tooltip title={tt('Bu hatırlatmayı bugün için kapat', 'Dismiss for today')}>
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={() => dismiss(group.source)}
                      aria-label={tt('Kapat', 'Close')}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              sx={{ width: '100%' }}
            >
              <AlertTitle>
                {group.direction === 'RECEIVABLE' ? tt('TAHSİLAT — ', 'COLLECTION — ') : ''}
                {title}
              </AlertTitle>
              {detail}
              {extra}
            </Alert>
          );
        })}
      </Stack>
    </Snackbar>
  );
};

export default DueRemindersNotifier;
